import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { formatAUD, formatDateAU } from '../../../shared/format';
import { useNamedViews } from '../hooks/useSavedView';
import { SavedViewsMenu } from '../components/SavedViewsMenu';
import { QASkeleton } from '../components/QASkeleton';
import { QAEmpty } from '../components/QAEmpty';
import { BookGrid } from '../components/book/BookGrid';
import { BookCards } from '../components/book/BookCards';
import { CellEditor, type CellEdit } from '../components/book/CellEditor';
import { LogCallEditor, type NoteType } from '../components/book/LogCallEditor';
import { BulkBar } from '../components/book/BulkBar';
import { mergeSettings, type PracticeSettings } from '../../../shared/practiceSettings';
import { applyEdit, currentValue, editTargetFor, patchBody, type EditTarget } from '../../../shared/bookEdit';
import { windowsInEffect } from '../../../shared/compliance';
import { nextCallDue } from '../../../shared/cadence';
import {
  BASE_COLUMNS, COMPLIANCE_COLUMNS, allColumns, applyBookQuery, isFilterActive, normaliseWatchCodes, toCsv, todayBrisbane,
  type BookColumn, type FilterMap,
} from '../../../shared/bookGrid';
import { QAChartTooltip } from '../components/QAChartTooltip';
import type { DashboardRow, DashboardSummary } from '../../../shared/types';
import type { ClientRecord } from '../../../shared/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';

interface View {
  q: string;
  sort: { key: string; dir: 'asc' | 'desc' };
  /** Per-column autofilters (Excel-style), keyed by BookColumn.key. */
  filters: FilterMap;
  /** Column keys switched off in the Columns menu. */
  hidden: string[];
  /** Watched security codes, each adding value / P/L / last-trade columns. */
  watch: string[];
}

const DEFAULT_HIDDEN = BASE_COLUMNS.filter((c) => c.defaultHidden).map((c) => c.key);
// Default mirrors the legacy sheet: every household, soonest call first.
const DEFAULT_VIEW: View = { q: '', sort: { key: 'callDue', dir: 'asc' }, filters: {}, hidden: DEFAULT_HIDDEN, watch: [] };

/** One-click shortcuts for the filters Scott reaches for most in the Excel sheet. */
const QUICK_FILTERS: Array<{ id: string; label: string; filters: FilterMap }> = [
  { id: 'calls', label: 'Calls due', filters: { callDue: { type: 'date', preset: 'dueBy7' } } },
  { id: 'open', label: 'Compliance outstanding', filters: { openItems: { type: 'range', min: 1 } } },
  { id: 'reviews', label: 'Reviews overdue', filters: { reviewDue: { type: 'date', preset: 'overdue' } } },
  { id: 'advice', label: 'SoA / RoA to write', filters: { 'c.writeSoa': { type: 'values', values: ['outstanding'] } } },
  { id: 'consent', label: 'Consents due', filters: { consentStatus: { type: 'values', values: ['Due soon', 'Renewal open', 'Lapsed'] } } },
];

const QA_COLOURS = ['#B8431A', '#A07A3F', '#2F4636', '#8A6418', '#7A6E63'];

export function Dashboard() {
  const { user } = useAuth();
  const viewApi = useNamedViews<View>('dashboard', DEFAULT_VIEW);
  // Views saved before the book grid carried other keys (segment, rpFilter,
  // outstandingOnly); coerce so a legacy view can never crash the page.
  const raw = viewApi.value;
  const view: View = {
    q: typeof raw.q === 'string' ? raw.q : '',
    sort: raw.sort && typeof raw.sort.key === 'string' ? raw.sort : DEFAULT_VIEW.sort,
    filters: raw.filters && typeof raw.filters === 'object' ? raw.filters : {},
    hidden: Array.isArray(raw.hidden) ? raw.hidden : DEFAULT_HIDDEN,
    watch: normaliseWatchCodes(Array.isArray(raw.watch) ? raw.watch : []),
  };
  const setView = (fn: (v: View) => View) => viewApi.setValue(() => fn(view));
  const watchKey = view.watch.join(',');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard', watchKey],
    queryFn: () => api<{ data: DashboardRow[]; summary: DashboardSummary }>(
      watchKey ? `/api/dashboard?watch=${encodeURIComponent(watchKey)}` : '/api/dashboard'),
    placeholderData: keepPreviousData,
  });

  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  // Phones only: the book as cards or as the full grid (remembered per browser).
  const [phoneView, setPhoneView] = useState<'grid' | 'cards'>(() => {
    try { return localStorage.getItem('cb.book.phoneView') === 'cards' ? 'cards' : 'grid'; } catch { return 'grid'; }
  });
  const choosePhoneView = (v: 'grid' | 'cards') => {
    setPhoneView(v);
    try { localStorage.setItem('cb.book.phoneView', v); } catch { /* storage blocked: keep for this visit */ }
  };
  const [watchDraft, setWatchDraft] = useState('');
  const canDelete = can(user?.role, 'client.delete');
  const archived = useQuery({
    queryKey: ['clients', 'archived'],
    queryFn: () => api<{ data: ClientRecord[] }>('/api/clients?archived=only&limit=500'),
    enabled: showArchived,
  });

  async function restoreClient(id: string) {
    try {
      await api(`/api/clients/${id}/restore`, { method: 'POST' });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['clients', 'archived'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    } catch { /* surfaced on next render */ }
  }

  async function purgeClient(id: string, name: string) {
    if (!window.confirm(`Permanently delete ${name}? This removes the client and ALL their accounts, holdings, transactions, file notes, advice and compliance records. This cannot be undone.`)) return;
    try {
      await api(`/api/clients/${id}`, { method: 'DELETE' });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['clients', 'archived'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    } catch { /* row stays */ }
  }

  const today = todayBrisbane();
  const columns = useMemo(
    () => allColumns(view.watch).filter((c) => c.key === 'name' || !view.hidden.includes(c.key)),
    [view.watch, view.hidden],
  );
  const filtered = useMemo(
    () => (data ? applyBookQuery(data.data, { q: view.q, filters: view.filters, sort: view.sort }, today) : []),
    [data, view.q, view.filters, view.sort, today],
  );
  const activeFilterCount = Object.values(view.filters).filter(isFilterActive).length;

  const segmentChart = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, number>();
    for (const r of data.data) {
      const seg = r.segment ?? 'unsegmented';
      map.set(seg, (map.get(seg) ?? 0) + 1);
    }
    return [...map.entries()].map(([name, value]) => ({ name, value }));
  }, [data]);

  const outstandingChart = useMemo(() => {
    if (!data) return [];
    const o = data.summary.outstandingCount;
    return COMPLIANCE_COLUMNS.map((c) => ({ name: c.label, count: o[c.key] ?? 0 }));
  }, [data]);

  const canEdit = can(user?.role, 'client.edit');
  const [editing, setEditing] = useState<{ row: DashboardRow; target: EditTarget; anchor: HTMLElement } | null>(null);
  const [toast, setToast] = useState<{ text: string; undo?: () => void; error?: boolean } | null>(null);
  const [logging, setLogging] = useState<{ row: DashboardRow; anchor: HTMLElement } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  function selectIds(ids: string[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) { if (on) next.add(id); else next.delete(id); }
      return next;
    });
  }
  const settingsQ = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<{ data: PracticeSettings }>('/api/settings'),
    staleTime: 60_000,
  });
  const settings = mergeSettings(settingsQ.data?.data);
  const windows = windowsInEffect(settings.compliance.versions, today);
  const toastTimer = useRef<number | undefined>(undefined);
  function showToast(t: { text: string; undo?: () => void; error?: boolean }) {
    window.clearTimeout(toastTimer.current);
    setToast(t);
    toastTimer.current = window.setTimeout(() => setToast(null), 8000);
  }
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  type DashData = { data: DashboardRow[]; summary: DashboardSummary };
  const dashKey = ['dashboard', watchKey];
  function patchRow(clientId: string, fn: (r: DashboardRow) => DashboardRow) {
    qc.setQueryData<DashData>(dashKey, (old) => old && { ...old, data: old.data.map((r) => (r.clientId === clientId ? fn(r) : r)) });
  }

  /**
   * Save one in-grid edit. Optimistic: the cell flips straight away (compliance
   * recomputed by the shared rules), rolls back if the server refuses, and the
   * book refetches in the background so the server stays authoritative.
   */
  async function saveEdit(row: DashboardRow, target: EditTarget, edit: CellEdit, opts: { quiet?: boolean } = {}) {
    const before = qc.getQueryData<DashData>(dashKey);
    const prev = currentValue(row, target);
    const optimisticValue = edit.kind === 'note' ? today : edit.value;
    patchRow(row.clientId, (r) => applyEdit(r, target, optimisticValue, new Date(), windows));
    try {
      if (edit.kind === 'note') {
        await api(`/api/clients/${row.clientId}/file-notes`, {
          method: 'POST',
          body: JSON.stringify({ type: edit.type, content: edit.content }),
        });
      } else {
        await api(`/api/clients/${row.clientId}/compliance`, {
          method: 'PATCH',
          body: JSON.stringify(patchBody(target, edit.value)),
        });
      }
    } catch (e) {
      if (before) qc.setQueryData(dashKey, before);
      throw e;
    } finally {
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    }
    if (opts.quiet) return;
    const who = row.sortName ?? row.name;
    const what = edit.kind === 'note'
      ? `File note added for ${who}`
      : target.kind === 'flag'
        ? `${target.label} ${edit.value ? 'flagged' : 'marked done'} for ${who}`
        : edit.value
          ? `${target.label} set to ${formatDateAU(String(edit.value))} for ${who}`
          : `${target.label} cleared for ${who}`;
    // Notes are real records; everything else is a field change we can put back.
    const undo = edit.kind === 'note' ? undefined : () => {
      setToast(null);
      saveEdit(applyEdit(row, target, edit.value, new Date(), windows), target, { kind: 'value', value: prev }, { quiet: true })
        .then(() => showToast({ text: `Undone: ${target.label} for ${who}` }))
        .catch(() => showToast({ text: `Could not undo ${target.label} for ${who}`, error: true }));
    };
    showToast({ text: what, undo });
  }

  /** Log a call: note + Call Due rolled by tier. Optimistic, with an undo for the Call Due change. */
  async function saveLogCall(row: DashboardRow, input: { type: NoteType; content: string; rollCallDue: boolean }) {
    const before = qc.getQueryData<DashData>(dashKey);
    const noteTarget = { kind: 'note' as const, column: 'c.fileNote', label: 'File Note' };
    patchRow(row.clientId, (r) => {
      const withNote = applyEdit(r, noteTarget, today, new Date(), windows);
      return input.rollCallDue ? { ...withNote, callDue: nextCallDueFor(r) } : withNote;
    });
    let res: { data: { callDue: string | null; previousCallDue: string | null } };
    try {
      res = await api(`/api/clients/${row.clientId}/log-call`, { method: 'POST', body: JSON.stringify(input) });
    } catch (e) {
      if (before) qc.setQueryData(dashKey, before);
      throw e;
    } finally {
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    }
    const who = row.sortName ?? row.name;
    const { callDue, previousCallDue } = res.data;
    const moved = callDue !== previousCallDue;
    showToast({
      text: moved && callDue ? `Call logged for ${who}. Next call ${formatDateAU(callDue)}` : `Call logged for ${who}`,
      undo: moved ? () => {
        setToast(null);
        api(`/api/clients/${row.clientId}/compliance`, { method: 'PATCH', body: JSON.stringify({ callDue: previousCallDue }) })
          .then(() => { void qc.invalidateQueries({ queryKey: ['dashboard'] }); showToast({ text: `Call Due put back for ${who} (the note stays)` }); })
          .catch(() => showToast({ text: `Could not put Call Due back for ${who}`, error: true }));
      } : undefined,
    });
  }
  function nextCallDueFor(r: DashboardRow): string | null {
    return nextCallDue(r.segment, today, settings) ?? r.callDue;
  }

  function exportCsv() {
    const csv = toCsv(filtered, columns, today);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `client-book-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function addWatch() {
    const codes = normaliseWatchCodes([...view.watch, ...watchDraft.split(/[\s,]+/)]);
    setView((v) => ({ ...v, watch: codes }));
    setWatchDraft('');
  }

  // Wait for saved views to hydrate from the server too: rendering the grid
  // first let an early filter/sort click be overwritten when the stored view
  // arrived a moment later.
  if (isLoading || viewApi.hydrating) return <QASkeleton variant="dashboard" />;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load dashboard</div>;

  const summary = data.summary;
  const quickOn = (qf: (typeof QUICK_FILTERS)[number]) =>
    Object.entries(qf.filters).every(([k, f]) => JSON.stringify(view.filters[k]) === JSON.stringify(f));

  return (
    <div>
      <div className="mb-5 qa-rise qa-stagger-1 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="qa-eyebrow mb-2">Client book</p>
          <h1 className="qa-h1">
            Your book, <span className="qa-italic-accent">today.</span>
          </h1>
          <p className="qa-deck mt-2 text-ink-soft">
            {summary.clientCount} clients · {formatAUD(summary.totalTnw, { compact: true })} total net worth · {formatAUD(summary.totalCash, { compact: true })} in cash
          </p>
          <PriceFreshness prices={summary.prices} fxMissing={summary.fxMissing} />
        </div>
        {can(user?.role, 'client.edit') && (
          <Link to="/clients/new" className="btn-primary self-start md:self-end whitespace-nowrap">
            + New client
          </Link>
        )}
      </div>

      {/* Admin attention banner: unlinked file notes / open duplicate matches. */}
      {can(user?.role, 'users.manage') && (
        (summary.attentionCounts?.unlinkedFileNotes ?? 0) > 0 ||
        (summary.attentionCounts?.openMatchCandidates ?? 0) > 0
      ) && (
        <div className="qa-attention-banner mb-5 qa-rise qa-stagger-1">
          <span className="qa-eyebrow text-xs text-caution">Needs attention</span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-1">
            {(summary.attentionCounts?.unlinkedFileNotes ?? 0) > 0 && (
              <Link to="/admin/unlinked-file-notes"
                className="text-sm text-ink hover:text-rust underline decoration-hairline underline-offset-4">
                <span className="qa-mono">{summary.attentionCounts!.unlinkedFileNotes}</span>
                {' '}unlinked file note{summary.attentionCounts!.unlinkedFileNotes === 1 ? '' : 's'} →
              </Link>
            )}
            {(summary.attentionCounts?.openMatchCandidates ?? 0) > 0 && (
              <Link to="/admin/match-candidates"
                className="text-sm text-ink hover:text-rust underline decoration-hairline underline-offset-4">
                <span className="qa-mono">{summary.attentionCounts!.openMatchCandidates}</span>
                {' '}open duplicate match{summary.attentionCounts!.openMatchCandidates === 1 ? '' : 'es'} →
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="qa-book-toolbar flex flex-wrap gap-2 mb-2 items-center qa-rise qa-stagger-2">
        <input type="search" placeholder="Search name or email" aria-label="Search clients" className="qa-input w-full sm:w-64"
          value={view.q} onChange={e => setView(v => ({ ...v, q: e.target.value }))} />
        <SavedViewsMenu api={viewApi} label="View" />
        <ColumnsMenu columns={allColumns(view.watch)} hidden={view.hidden}
          onChange={(hidden) => setView(v => ({ ...v, hidden }))} />
        <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); addWatch(); }}>
          <input className="qa-input w-32 qa-mono uppercase" placeholder="Watch code" aria-label="Add a security to watch, e.g. NUF"
            value={watchDraft} onChange={e => setWatchDraft(e.target.value)} />
          <button type="submit" className="btn-ghost text-xs" disabled={!watchDraft.trim()}>+ Watch</button>
        </form>
        {view.watch.map(code => (
          <span key={code} className="qa-book-chip">
            {code}
            <button type="button" aria-label={`Stop watching ${code}`}
              onClick={() => setView(v => ({
                ...v,
                watch: v.watch.filter(c => c !== code),
                filters: Object.fromEntries(Object.entries(v.filters).filter(([k]) => !k.startsWith(`w:${code}:`))),
              }))}>×</button>
          </span>
        ))}
        <div className="flex-1" />
        <button type="button" className="btn-ghost text-xs whitespace-nowrap" onClick={exportCsv}>Export CSV</button>
        <button type="button" className="btn-ghost text-xs whitespace-nowrap" aria-pressed={showArchived} onClick={() => setShowArchived(s => !s)}>
          {showArchived ? 'Hide archived' : 'Archived'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3 items-center text-xs">
        {QUICK_FILTERS.map(qf => {
          const on = quickOn(qf);
          return (
            <button key={qf.id} type="button" aria-pressed={on}
              className={`qa-book-quick ${on ? 'is-on' : ''}`}
              onClick={() => setView(v => {
                const filters = { ...v.filters };
                if (on) for (const k of Object.keys(qf.filters)) delete filters[k];
                else Object.assign(filters, qf.filters);
                return { ...v, filters };
              })}>
              {qf.label}
            </button>
          );
        })}
        {(activeFilterCount > 0 || view.q) && (
          <button type="button" className="text-rust underline underline-offset-4 ml-1"
            onClick={() => setView(v => ({ ...v, q: '', filters: {} }))}>
            Clear {activeFilterCount > 0 ? `${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'}` : 'search'}
          </button>
        )}
        <span className="ml-auto text-ink-mute hidden sm:inline">Double-click a row to open the client</span>
        <span className="ml-auto sm:hidden qa-tabs" role="group" aria-label="Book layout on this phone">
          <button type="button" className={`qa-tab ${phoneView === 'cards' ? 'qa-tab--active' : ''}`} aria-pressed={phoneView === 'cards'} onClick={() => choosePhoneView('cards')}>Cards</button>
          <button type="button" className={`qa-tab ${phoneView === 'grid' ? 'qa-tab--active' : ''}`} aria-pressed={phoneView === 'grid'} onClick={() => choosePhoneView('grid')}>Grid</button>
        </span>
      </div>

      {(() => {
        // Bulk actions apply to what's selected AND visible, so a filter can't hide a target.
        const visible = filtered.filter((r) => selected.has(r.clientId)).map((r) => r.clientId);
        const hidden = selected.size - visible.length;
        return visible.length > 0 ? (
          <>
            <BulkBar
              ids={visible}
              today={today}
              canEdit={canEdit}
              canExport={can(user?.role, 'export.run')}
              onDone={(text, error) => { showToast({ text, error }); void qc.invalidateQueries({ queryKey: ['dashboard'] }); }}
              onClear={() => setSelected(new Set())}
            />
            {hidden > 0 && <p className="text-xs text-ink-mute mb-2">{hidden} selected {hidden === 1 ? 'household is' : 'households are'} hidden by the current filters and won't be included.</p>}
          </>
        ) : null;
      })()}

      {phoneView === 'cards' && (
        <div className="sm:hidden">
          <BookCards rows={filtered} total={data.data.length} today={today}
            onLogCall={canEdit ? (row, anchor) => setLogging({ row, anchor }) : undefined} />
        </div>
      )}
      <div className={phoneView === 'cards' ? 'hidden sm:block' : ''}>
      <BookGrid
        selected={selected}
        onSelect={selectIds}
        rows={filtered}
        allRows={data.data}
        columns={columns}
        today={today}
        q={view.q}
        filters={view.filters}
        sort={view.sort}
        onFilters={(filters) => setView(v => ({ ...v, filters }))}
        onSort={(sort) => setView(v => ({ ...v, sort }))}
        onEdit={canEdit ? (row, target, anchor) => setEditing({ row, target: editTargetFor(target.column, windows) ?? target, anchor }) : undefined}
        onLogCall={canEdit ? (row, anchor) => setLogging({ row, anchor }) : undefined}
      />
      </div>

      {logging && (
        <LogCallEditor
          row={logging.row}
          anchor={logging.anchor}
          today={today}
          settings={settings}
          onSave={(input) => saveLogCall(logging.row, input)}
          onClose={() => setLogging(null)}
        />
      )}

      {editing && (
        <CellEditor
          row={editing.row}
          target={editing.target}
          anchor={editing.anchor}
          today={today}
          onSave={(edit) => saveEdit(editing.row, editing.target, edit)}
          onClose={() => setEditing(null)}
        />
      )}

      {toast && (
        <div className={`qa-book-toast ${toast.error ? 'is-error' : ''}`} role="status" aria-live="polite">
          <span>{toast.text}</span>
          {toast.undo && <button type="button" onClick={toast.undo}>Undo</button>}
          <button type="button" aria-label="Dismiss" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {/* Book insights: the old chart row, now secondary to the grid. */}
      <div className="mt-6">
        <button type="button" className="qa-eyebrow text-ink-mute hover:text-ink" aria-expanded={showInsights}
          onClick={() => setShowInsights(s => !s)}>
          {showInsights ? '▾' : '▸'} Book insights
        </button>
        {showInsights && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-3">
            <div className="qa-card p-5 lg:col-span-2">
              <p className="qa-eyebrow mb-3">Outstanding compliance by rule</p>
              <div className="h-56" aria-hidden="true" inert>
                <ResponsiveContainer>
                  <BarChart data={outstandingChart}>
                    <CartesianGrid stroke="rgba(26,22,20,0.06)" vertical={false} />
                    <XAxis dataKey="name" stroke="#7A6E63" tick={{ fontFamily: 'JetBrains Mono', fontSize: 10, fill: '#7A6E63' }} />
                    <YAxis stroke="#7A6E63" allowDecimals={false} tick={{ fontFamily: 'JetBrains Mono', fontSize: 10, fill: '#7A6E63' }} />
                    <Tooltip cursor={{ fill: 'rgba(184,67,26,0.06)' }} content={<QAChartTooltip />} />
                    <Bar dataKey="count" name="Outstanding" fill="#B8431A" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="qa-card p-5">
              <p className="qa-eyebrow mb-3">Segment mix</p>
              <div className="h-56" aria-hidden="true" inert>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={segmentChart} dataKey="value" nameKey="name" innerRadius={42} outerRadius={70} stroke="#FBF7EF">
                      {segmentChart.map((_, i) => <Cell key={i} fill={QA_COLOURS[i % QA_COLOURS.length]} />)}
                    </Pie>
                    <Tooltip content={<QAChartTooltip valueFormat={(v) => `${v} client${Number(v) === 1 ? '' : 's'}`} />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>

      {showArchived && (
        <div className="mt-8 qa-rise">
          <p className="qa-eyebrow mb-3">Archived clients</p>
          {archived.isLoading ? (
            <div className="text-xs text-ink-mute">Loading archived clients…</div>
          ) : (archived.data?.data.length ?? 0) === 0 ? (
            <QAEmpty inline title="No archived clients." />
          ) : (
            <div className="overflow-x-auto border border-hairline">
              <table className="qa-table min-w-[700px]">
                <thead>
                  <tr><th>Client</th><th>Seg</th><th>Archived</th><th className="text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {archived.data!.data.map(r => (
                    <tr key={r.id}>
                      <td>
                        <span className="text-ink font-medium">{r.surname?.toUpperCase()}, {r.firstName}</span>
                        <div className="text-xs text-ink-mute truncate max-w-xs">{r.email1 ?? '—'}</div>
                      </td>
                      <td>{r.segment ?? '—'}</td>
                      <td>{formatDateAU(r.deletedAt)}</td>
                      <td className="text-right whitespace-nowrap">
                        <button className="btn-ghost text-xs" onClick={() => restoreClient(r.id)}>Restore</button>
                        {canDelete && (
                          <button
                            className="btn-ghost text-xs text-rose-700 hover:text-rose-800 ml-2"
                            onClick={() => purgeClient(r.id, `${r.firstName} ${r.surname}`)}
                          >
                            Delete permanently
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ColumnsMenu({ columns, hidden, onChange }: { columns: BookColumn[]; hidden: string[]; onChange: (hidden: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const toggleable = columns.filter(c => c.key !== 'name');
  return (
    <div ref={ref} className="relative">
      <button type="button" className="btn-ghost text-xs" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        Columns{hidden.length ? ` (${toggleable.length - toggleable.filter(c => hidden.includes(c.key)).length}/${toggleable.length})` : ''}
      </button>
      {open && (
        <div className="qa-book-pop qa-shadow" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4 }} role="dialog" aria-label="Show or hide columns">
          <div className="max-h-80 overflow-y-auto">
            {toggleable.map(c => (
              <label key={c.key} className="qa-book-pop__opt">
                <input type="checkbox" checked={!hidden.includes(c.key)}
                  onChange={() => onChange(hidden.includes(c.key) ? hidden.filter(k => k !== c.key) : [...hidden, c.key])} />
                {c.label}
              </label>
            ))}
          </div>
          <div className="flex justify-between mt-2 pt-2 border-t border-hairline-paper">
            <button type="button" className="qa-book-pop__link" onClick={() => onChange([])}>Show all</button>
            <button type="button" className="qa-book-pop__link" onClick={() => onChange(DEFAULT_HIDDEN)}>Reset</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * How current the prices behind TNW are. Loud when any held security's price is
 * over 4 days old (a dead feed looked healthy for three months before this).
 */
function PriceFreshness({ prices, fxMissing }: { prices?: DashboardSummary['prices']; fxMissing?: string[] }) {
  if (!prices || prices.held === 0) return null;
  const fmt = (iso: string | null) => (iso
    ? new Date(iso).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', day: '2-digit', month: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : 'never');
  const parts: string[] = [];
  if (prices.stale > 0) parts.push(`${prices.stale} of ${prices.held} held securities have feed prices over 4 days old (oldest ${fmt(prices.oldest)}). Values may be out of date.`);
  if ((prices.unpriced ?? 0) > 0) parts.push(`No price for ${prices.unpricedCodes?.join(', ')}: counted as $0. Set a price on the client's Holdings tab.`);
  if ((prices.manualStale ?? 0) > 0) parts.push(`${prices.manualStale} hand-priced fund${prices.manualStale === 1 ? '' : 's'} not updated in over a month.`);
  const loud = parts.length > 0;
  return (
    <p className={`qa-book-prices mt-1 ${loud ? 'is-stale' : ''}`} data-testid="price-freshness">
      {loud ? parts.join(' ') : `Prices as at ${fmt(prices.newest)}.`}
      {fxMissing && fxMissing.length > 0 && ` No FX rate for ${fxMissing.join(', ')}: those holdings are shown unconverted.`}
    </p>
  );
}
